import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

type Dict = Record<string, string>;
const SUPPORTED = ["en", "fr"] as const;
type Locale = (typeof SUPPORTED)[number];
const DEFAULT: Locale = "en";
const STORAGE_KEY = "tcgpricer_locale";

interface I18nCtx {
  locale: Locale;
  t: (key: string, vars?: Record<string, string | number>) => string;
  setLocale: (l: Locale) => void;
  fmtDate: (v: string | Date | number | null | undefined) => string;
  fmtNumber: (v: number | string | null | undefined, opts?: Intl.NumberFormatOptions) => string;
  fmtMoney: (v: number | string | null | undefined, currency?: string) => string;
}

const Ctx = createContext<I18nCtx | null>(null);

function detect(): Locale {
  if (typeof window === "undefined") return DEFAULT;
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && (SUPPORTED as readonly string[]).includes(stored)) return stored as Locale;
  const nav = String(navigator.language || "").slice(0, 2).toLowerCase();
  if ((SUPPORTED as readonly string[]).includes(nav)) return nav as Locale;
  return DEFAULT;
}

async function loadDict(loc: Locale): Promise<Dict> {
  try {
    const r = await fetch(`/i18n/${loc}.json`, { cache: "no-cache" });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return await r.json();
  } catch (e) {
    console.warn("[i18n] cannot load", loc, e);
    return {};
  }
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => detect());
  const [dict, setDict] = useState<Dict>({});

  useEffect(() => {
    document.documentElement.lang = locale;
    localStorage.setItem(STORAGE_KEY, locale);
    loadDict(locale).then(setDict);
  }, [locale]);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      let s = dict[key] ?? key;
      if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
      return s;
    },
    [dict],
  );

  const tag = locale === "fr" ? "fr-FR" : "en-US";

  const fmtDate = useCallback(
    (v: string | Date | number | null | undefined) => {
      if (v == null || v === "") return "—";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return String(v);
      return d.toLocaleString(tag, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    },
    [tag],
  );

  const fmtNumber = useCallback(
    (v: number | string | null | undefined, opts?: Intl.NumberFormatOptions) => {
      if (v == null || v === "") return "";
      const n = Number(v);
      if (!Number.isFinite(n)) return String(v);
      return n.toLocaleString(tag, opts);
    },
    [tag],
  );

  const fmtMoney = useCallback(
    (v: number | string | null | undefined, currency = "EUR") => {
      if (v == null || v === "") return "";
      const n = Number(v);
      if (!Number.isFinite(n)) return String(v);
      return n.toLocaleString(tag, { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    [tag],
  );

  const value = useMemo<I18nCtx>(
    () => ({ locale, setLocale: setLocaleState, t, fmtDate, fmtNumber, fmtMoney }),
    [locale, t, fmtDate, fmtNumber, fmtMoney],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useI18n must be used within I18nProvider");
  return c;
}

export { SUPPORTED as SUPPORTED_LOCALES };
export type { Locale };
